import FilterContainer, { FilterProps } from '../filter-container';
import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  CheckboxFontSize,
  FlexBox,
  FlexColumn,
} from '@make-software/cspr-design';
import { BodyText, Tooltip } from '@block-explorer/ui';
import styled from 'styled-components';
import isEqual from 'fast-deep-equal';
import { uniq } from 'remeda';
import { useFilterQuery } from '../filter-query';
import { truncateName } from '../../action-messages/utils';

const InputContainer = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    flexDirection: ['column', 'row'],
    alignItems: 'flex-start',
    borderBottom: theme.border.separator,
    paddingBottom: 12,
    maxHeight: 208,
    overflowY: 'auto',
    '> * + *': {
      marginLeft: [0, 24],
      marginTop: [10, 0],
    },
  })
);

type Option<T> = { value: T; label: string };

interface OptionFilterProps<T> extends FilterProps {
  filterId: string;
  label?: string;
  options: Option<T>[];
}

const getNormalizedQueryFilterValues = <T,>(
  value: unknown,
  options: Option<T>[]
): T[] => {
  if (value == null) {
    return [];
  }

  const hasArrayOptions = options.some(({ value }) => Array.isArray(value));
  const rawValues = Array.isArray(value)
    ? hasArrayOptions && !value.some(Array.isArray)
      ? [value]
      : value
    : [value];

  return rawValues.filter((queryValue) =>
    options.some((option) => isEqual(option.value, queryValue))
  ) as T[];
};

const OPTION_ITEM_LENGTH_LIMIT = 30;

export const OptionFilter = <T,>({
  filterId,
  filters,
  setFilters,
  options,
  label,
}: OptionFilterProps<T>) => {
  const { queryFilters, syncFiltersToQuery } = useFilterQuery();
  const queryFilterValue = queryFilters?.[filterId];
  const initialFilter =
    (filters?.[filterId] as unknown as T[]) ||
    getNormalizedQueryFilterValues<T>(queryFilterValue, options);

  const [inputValue, setInputValue] = useState<T[]>(initialFilter);
  const [appliedInputValue, setAppliedInputValue] =
    useState<T[]>(initialFilter);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (queryFilterValue == null) {
      return;
    }

    const nextInputValue = getNormalizedQueryFilterValues<T>(
      queryFilterValue,
      options
    );
    if (
      !isEqual(inputValue, nextInputValue) ||
      !isEqual(appliedInputValue, nextInputValue)
    ) {
      setInputValue(nextInputValue);
      setAppliedInputValue(nextInputValue);
      setError(null);
    }

    const nextFilters = nextInputValue.length
      ? { ...(filters || {}), [filterId]: nextInputValue }
      : Object.fromEntries(
          Object.entries(filters || {}).filter(([key]) => key !== filterId)
        );

    if (!isEqual(filters?.[filterId], nextInputValue)) {
      setFilters(nextFilters);
    }
  }, [queryFilterValue, filterId, options]);

  const hasChanges = !isEqual(inputValue, appliedInputValue);
  const disabled = !!error || (!hasChanges && appliedInputValue.length === 0);

  const resetFilters = () => {
    setInputValue([]);
    setAppliedInputValue([]);
    setError(null);

    const { [filterId]: _, ...rest } = filters || {};
    setFilters(rest);
    syncFiltersToQuery(rest);
  };

  const handleSetFilters = () => {
    if (inputValue.length === 0) {
      resetFilters();
    } else {
      setAppliedInputValue(inputValue);
      const newFilters = { ...(filters || {}), [filterId]: inputValue };
      setFilters(newFilters);
      syncFiltersToQuery(newFilters);
    }
  };

  const handleChooseFilterOption = (value) => {
    const isSelected = inputValue.some((option) => isEqual(option, value));

    setInputValue(
      isSelected
        ? inputValue.filter((option) => !isEqual(option, value))
        : uniq([...inputValue, value])
    );
  };

  const isFilterSet =
    Object.keys(filters || {})?.includes(filterId) &&
    !!filters?.[filterId]?.length;

  const truncatedLabel = (label: string) => {
    const hasTooltip = label.length > OPTION_ITEM_LENGTH_LIMIT;
    return hasTooltip ? (
      <Tooltip tooltipContent={label}>
        <BodyText size={3} wordBreak noWrap>
          {truncateName(label, OPTION_ITEM_LENGTH_LIMIT)}
        </BodyText>
      </Tooltip>
    ) : (
      <BodyText size={3} wordBreak noWrap>
        {label}
      </BodyText>
    );
  };

  return (
    <FilterContainer
      isFilterSet={isFilterSet}
      onSetFilters={handleSetFilters}
      onResetFilters={resetFilters}
      onResetErrors={() => setError(null)}
      disabled={disabled}
    >
      <FlexColumn itemsSpacing={12}>
        {label && (
          <BodyText size={1} variation={'black'}>
            {label}
          </BodyText>
        )}
        <InputContainer>
          <FlexColumn itemsSpacing={12}>
            {options.map(({ value, label }) => (
              <Checkbox
                key={'option-cb-' + label.toLowerCase()}
                type="checkbox"
                onChange={() => handleChooseFilterOption(value)}
                label={truncatedLabel(label)}
                checked={
                  inputValue.findIndex((option) => isEqual(option, value)) !==
                  -1
                }
                checkboxFontSize={CheckboxFontSize.small}
              />
            ))}
          </FlexColumn>
        </InputContainer>
      </FlexColumn>
    </FilterContainer>
  );
};
